import { useEffect, useState } from "react";

export default function Hero() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToNext = () => {
    window.scrollTo({ top: window.innerHeight, behavior: "smooth" });
  };

  return (
    <section className="relative w-full min-h-screen flex flex-col overflow-hidden">

      {/* ── Nav ── */}
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          scrolled ? "bg-black/75 backdrop-blur-md" : "bg-transparent"
        }`}
      >
        <div className="w-full px-6 md:px-12 h-20 flex items-center justify-between">
          <a href="/" className="flex items-center">
            <img
              src="https://storage.readdy-site.link/project_files/c46f7f07-593f-4f17-9a57-3ac86083c18c/8ad1d54c-f962-4091-84fc-ce9d0111f123_1-4.png?v=f770e7fb4e5aaa93207d098e21607861"
              alt="Mission Beach Dive Adventures logo"
              className="h-12 w-auto object-contain"
              width="240"
              height="48"
              sizes="240px"
              fetchPriority="high"
              decoding="async"
            />
          </a>

          {/* Desktop nav */}
          <div className="hidden md:flex items-center gap-8 text-white/70 text-sm font-light">
            <a href="#experience" className="hover:text-white transition-colors cursor-pointer">Experience</a>
            <a href="#how-it-works" className="hover:text-white transition-colors cursor-pointer">How It Works</a>
            <a href="#reviews" className="hover:text-white transition-colors cursor-pointer">Reviews</a>
            <a href="#faq" className="hover:text-white transition-colors cursor-pointer">FAQ</a>
          </div>

          <a
            href="https://wa.me/61455957263?text=Hi!%20I'd%20like%20to%20book%20a%20dive%20trip%20to%20Bedarra%20Island."
            target="_blank"
            rel="noopener noreferrer"
            className="whitespace-nowrap hidden md:inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-orange-500 hover:bg-orange-600 text-white text-sm font-semibold cursor-pointer transition-all duration-300 hover:scale-105"
          >
            <i className="ri-calendar-check-line" />
            Book Now
          </a>

          {/* Mobile hamburger */}
          <button
            className="md:hidden text-white w-8 h-8 flex items-center justify-center cursor-pointer"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle menu"
          >
            <i className={`text-xl ${mobileMenuOpen ? "ri-close-line" : "ri-menu-line"}`} />
          </button>
        </div>

        {/* Mobile menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-black/90 backdrop-blur-md px-6 pb-6 flex flex-col gap-4 text-white/80 text-sm">
            <a href="#experience" onClick={() => setMobileMenuOpen(false)} className="py-2 border-b border-white/10 cursor-pointer">Experience</a>
            <a href="#how-it-works" onClick={() => setMobileMenuOpen(false)} className="py-2 border-b border-white/10 cursor-pointer">How It Works</a>
            <a href="#reviews" onClick={() => setMobileMenuOpen(false)} className="py-2 border-b border-white/10 cursor-pointer">Reviews</a>
            <a href="#faq" onClick={() => setMobileMenuOpen(false)} className="py-2 cursor-pointer">FAQ</a>
            <a
              href="https://wa.me/61455957263?text=Hi!%20I'd%20like%20to%20book%20a%20dive%20trip%20to%20Bedarra%20Island."
              target="_blank"
              rel="noopener noreferrer"
              className="whitespace-nowrap mt-2 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-orange-500 text-white font-semibold cursor-pointer"
            >
              <i className="ri-calendar-check-line" />
              Book Now
            </a>
          </div>
        )}
      </nav>

      {/* ── Background: user's real photo ── */}
      {/* aspect-ratio tells the browser to reserve 16:9 space before the image loads,
          eliminating CLS on mobile. min-h-screen ensures it's always full-viewport on desktop. */}
      <div
        className="absolute inset-0 w-full h-full"
        style={{ aspectRatio: "16 / 9" }}
      >
        {/* <picture> + srcset serves a smaller image to mobile/tablet,
            saving 60-80% bandwidth on non-desktop devices */}
        <picture>
          <source
            media="(max-width: 640px)"
            srcSet="https://storage.readdy-site.link/project_files/c46f7f07-593f-4f17-9a57-3ac86083c18c/8a744d67-edcc-461c-a00f-cff00962dcb2_DJI_0125.jpg?v=75d888a170cc561f6e9c0beb2c51d415&w=768"
          />
          <source
            media="(max-width: 1280px)"
            srcSet="https://storage.readdy-site.link/project_files/c46f7f07-593f-4f17-9a57-3ac86083c18c/8a744d67-edcc-461c-a00f-cff00962dcb2_DJI_0125.jpg?v=75d888a170cc561f6e9c0beb2c51d415&w=1280"
          />
          <img
            src="https://storage.readdy-site.link/project_files/c46f7f07-593f-4f17-9a57-3ac86083c18c/8a744d67-edcc-461c-a00f-cff00962dcb2_DJI_0125.jpg?v=75d888a170cc561f6e9c0beb2c51d415"
            alt="Aerial drone view of the Great Barrier Reef near Bedarra Island, Mission Beach Queensland"
            className="w-full h-full object-cover object-center"
            width="1920"
            height="1080"
            sizes="100vw"
            fetchPriority="high"
            decoding="sync"
          />
        </picture>
        {/* Cinematic gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/30 to-black/70" />
        {/* Subtle vignette */}
        <div className="absolute inset-0" style={{ background: "radial-gradient(ellipse at center, transparent 40%, rgba(0,0,0,0.55) 100%)" }} />
      </div>

      {/* ── Hero Content ── */}
      <div className="relative z-10 flex-1 flex flex-col items-center justify-center text-center px-6 md:px-10 pt-24 pb-10">

        {/* Eyebrow badge */}
        <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-cyan-400/40 bg-cyan-400/10 backdrop-blur-sm mb-8">
          <span className="w-1.5 h-1.5 rounded-full bg-cyan-400 animate-pulse" />
          <span className="text-cyan-300 text-[11px] uppercase tracking-[3px] font-medium">
            For Bedarra Island Resort Guests
          </span>
        </div>

        {/* Main headline */}
        <h1
          className="text-4xl sm:text-5xl md:text-6xl lg:text-[5.5rem] font-black text-white leading-[1.0] max-w-5xl mb-6 tracking-tight"
          style={{ textShadow: "0 4px 40px rgba(0,0,0,0.6)" }}
        >
          The Reef Is<br />
          <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-300 to-teal-300">
            Right There.
          </span>
        </h1>

        {/* Sub-headline */}
        <p className="text-base md:text-xl text-white/85 max-w-2xl leading-relaxed mb-10 font-light">
          You&apos;ve chosen one of Australia&apos;s most extraordinary island escapes.
          Now add the world beneath the surface — pristine outer reef, untouched coral gardens,
          and marine life most people only see in documentaries.
        </p>

        {/* CTA Buttons */}
        <div className="flex flex-col sm:flex-row items-center gap-4 mb-14">
          <a
            href="https://wa.me/61455957263?text=Hi!%20I'd%20like%20to%20book%20a%20dive%20trip%20to%20Bedarra%20Island."
            target="_blank"
            rel="noopener noreferrer"
            className="whitespace-nowrap inline-flex items-center justify-center gap-2.5 px-8 py-4 rounded-full bg-gradient-to-r from-orange-500 to-amber-500 text-white font-bold text-base cursor-pointer hover:scale-105 transition-transform duration-300"
            style={{ boxShadow: "0 8px 32px rgba(249,115,22,0.45)" }}
          >
            <i className="ri-ship-2-line text-lg w-5 h-5 flex items-center justify-center" />
            Book Your Dive Day Trip
          </a>

          <a
            href="tel:0455957263"
            className="whitespace-nowrap inline-flex items-center justify-center gap-2.5 px-8 py-4 rounded-full border-2 border-white/60 text-white font-medium text-base cursor-pointer hover:bg-white/15 hover:border-white transition-all duration-300"
          >
            <i className="ri-phone-line text-lg w-5 h-5 flex items-center justify-center" />
            Call: 0455 957 263
          </a>
        </div>

        {/* Star rating strip */}
        <div className="flex items-center gap-2 mb-8">
          <div className="flex items-center gap-0.5">
            {[1,2,3,4,5].map(i => (
              <i key={i} className="ri-star-fill text-amber-400 text-sm" />
            ))}
          </div>
          <span className="text-white/70 text-sm font-light">
            <strong className="text-white font-semibold">4.8</strong> · 249 reviews
          </span>
        </div>

        {/* Trust strip */}
        <div className="flex flex-col sm:flex-row flex-wrap items-center justify-center gap-x-5 gap-y-2 text-white/50 text-[11px] uppercase tracking-widest">
          <span className="whitespace-nowrap flex items-center gap-1.5">
            <i className="ri-award-line text-cyan-400/70" />
            PADI Dive Centre
          </span>
          <span className="hidden sm:inline text-white/20">·</span>
          <span className="whitespace-nowrap flex items-center gap-1.5">
            <i className="ri-ship-line text-cyan-400/70" />
            MV Mahi-Mahi
          </span>
          <span className="hidden sm:inline text-white/20">·</span>
          <span className="whitespace-nowrap flex items-center gap-1.5">
            <i className="ri-map-pin-line text-cyan-400/70" />
            Clump Point Jetty, Mission Beach
          </span>
          <span className="hidden sm:inline text-white/20">·</span>
          <span className="whitespace-nowrap flex items-center gap-1.5">
            <i className="ri-compass-3-line text-cyan-400/70" />
            Outer Reef Specialists
          </span>
        </div>
      </div>

      {/* ── Scroll Indicator ── */}
      <div className="relative z-10 flex justify-center pb-8">
        <button
          onClick={scrollToNext}
          aria-label="Scroll down"
          className="flex flex-col items-center gap-1 text-white/40 hover:text-white/70 transition-colors cursor-pointer group"
        >
          <span className="text-[10px] uppercase tracking-widest">Explore</span>
          <i className="ri-arrow-down-line text-xl w-6 h-6 flex items-center justify-center animate-bounce" />
        </button>
      </div>
    </section>
  );
}
